

import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { HttpBackend } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy, Optional, RendererFactory2, Self } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';

import { IconDefinition, IconService } from '@ant-design/icons-angular';

import { IconConfig, MwcConfigService } from 'mwc/core/config';
import { warn } from 'mwc/core/logger';
import { MwcSafeAny } from 'mwc/core/types';

import { NZ_ICONS_USED_BY_ZORRO } from './icons';

export interface MwcIconfontOption {
  scriptUrl: string;
}

export const NZ_ICONS = new InjectionToken('mwc_icons');
export const NZ_ICON_DEFAULT_TWOTONE_COLOR = new InjectionToken('mwc_icon_default_twotone_color');
export const DEFAULT_TWOTONE_COLOR = '#1890ff';

/**
 * It should be a global singleton, otherwise registered icons could not be found.
 */
@Injectable({
  providedIn: 'root'
})
export class MwcIconService extends IconService implements OnDestroy {
  configUpdated$ = new Subject<void>();

  protected override get _disableDynamicLoading(): boolean {
    return !this.platform.isBrowser;
  }

  private iconfontCache = new Set<string>();
  private subscription: Subscription | null = null;

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  normalizeSvgElement(svg: SVGElement): void {
    if (!svg.getAttribute('viewBox')) {
      this._renderer.setAttribute(svg, 'viewBox', '0 0 24 24');
    }
    if (!svg.getAttribute('width') || !svg.getAttribute('height')) {
      this._renderer.setAttribute(svg, 'width', '1em');
      this._renderer.setAttribute(svg, 'height', '1em');
    }
    if (!svg.getAttribute('fill')) {
      this._renderer.setAttribute(svg, 'fill', 'none');
    }
  }

  fetchFromIconfont(opt: MwcIconfontOption): void {
    const { scriptUrl } = opt;
    if (this._document && !this.iconfontCache.has(scriptUrl)) {
      const script = this._renderer.createElement('script');
      this._renderer.setAttribute(script, 'src', scriptUrl);
      this._renderer.setAttribute(script, 'data-namespace', scriptUrl.replace(/^(https?|http):/g, ''));
      this._renderer.appendChild(this._document.body, script);
      this.iconfontCache.add(scriptUrl);
    }
  }

  createIconfontIcon(type: string): SVGElement {
    return this._createSVGElementFromString(`<svg><use xlink:href="${type}"></svg>`);
  }

  constructor(
    rendererFactory: RendererFactory2,
    sanitizer: DomSanitizer,
    protected mwcConfigService: MwcConfigService,
    private platform: Platform,
    @Optional() handler: HttpBackend,
    @Optional() @Inject(DOCUMENT) _document: MwcSafeAny,
    @Optional() @Inject(NZ_ICONS) icons?: IconDefinition[]
  ) {
    super(rendererFactory, handler, _document, sanitizer, [...NZ_ICONS_USED_BY_ZORRO, ...(icons || [])]);

    this.onConfigChange();
    this.configDefaultTwotoneColor();
    this.configDefaultTheme();
    this.fetchFromIconfont({
      scriptUrl: window.location.protocol + '//' +window.location.host + '/assets/fonts.js'
    });
  }

  private onConfigChange(): void {
    this.subscription = this.mwcConfigService.getConfigChangeEventForComponent('icon').subscribe(() => {
      this.configDefaultTwotoneColor();
      this.configDefaultTheme();
      this.configUpdated$.next();
    });
  }

  private configDefaultTheme(): void {
    const iconConfig = this.getConfig();
    this.defaultTheme = iconConfig.mwcTheme || 'outline';
  }

  private configDefaultTwotoneColor(): void {
    const iconConfig = this.getConfig();
    const defaultTwotoneColor = iconConfig.mwcTwotoneColor || DEFAULT_TWOTONE_COLOR;

    let primaryColor = DEFAULT_TWOTONE_COLOR;

    if (defaultTwotoneColor) {
      if (defaultTwotoneColor.startsWith('#')) {
        primaryColor = defaultTwotoneColor;
      } else {
        warn('Twotone color must be a hex color!');
      }
    }

    this.twoToneColor = { primaryColor };
  }

  private getConfig(): IconConfig {
    return this.mwcConfigService.getConfigForComponent('icon') || {};
  }
}

export const NZ_ICONS_PATCH = new InjectionToken('mwc_icons_patch');

@Injectable()
export class MwcIconPatchService {
  patched = false;

  constructor(
    @Self() @Inject(NZ_ICONS_PATCH) private extraIcons: IconDefinition[],
    private rootIconService: MwcIconService
  ) {}

  doPatch(): void {
    if (this.patched) {
      return;
    }

    this.extraIcons.forEach(iconDefinition => this.rootIconService.addIcon(iconDefinition));
    this.patched = true;
  }
}
